import { useEffect } from 'react';
import { useController } from 'react-hook-form';
import { FONT_FACE_STYLES } from '@optimus-web/fonts';
import { FieldProps, Stack, StackItem, getPaymentInputStyles } from '@optimus-web/core';
import { FormInputField, PciProxyService, getErrorComponent, lt, usePciProxyContext } from '@mews/framework';
import withField from '@optimus-web/core/src/Field/Field';
import { CARD_NUMBER_FIELD_ID } from '../../../../modules/PciProxyPaymentCardFormSection/constants';
import { PaymentCardExpirationField } from '../../../common/PaymentCardExpirationField';
import { useGetSupportedGatewayCardTypesSuspense } from '../queries';
import { TokenizeCardFormField } from './schema';

export const TokenizeCardFormFields = () => {
    const pciProxy = usePciProxyContext();

    const {
        data: { storage },
        status,
    } = useGetSupportedGatewayCardTypesSuspense();

    const merchantId = storage.publicKey;

    useEffect(() => {
        if (!merchantId) {
            return;
        }

        void (async () => {
            await pciProxy.load();
            pciProxy.init({
                merchantId,
                cardNumberFieldId: CARD_NUMBER_FIELD_ID,
                styles: {
                    '*': getPaymentInputStyles(),
                    '@font-face': {
                        '*': FONT_FACE_STYLES,
                    },
                },
                options: {},
            });
        })();

        return () => {
            pciProxy.reset();
        };
    }, [merchantId, pciProxy]);

    if (status !== 'success') {
        return null;
    }

    return (
        <Stack spacing="300" vertical>
            <Stack spacing="100">
                <StackItem grow>
                    <PaymentCardNumberField
                        name={TokenizeCardFormField.PaymentCardNumber}
                        label={lt('CardNumber')}
                        required
                        pciProxy={pciProxy}
                        helper={lt('ProcessPaymentMewsPaymentCardPaymentCardNumberInputHint')}
                    />
                </StackItem>
                <PaymentCardExpirationField
                    name={TokenizeCardFormField.PaymentCardExpiration}
                    label={lt('ProcessPaymentMewsPaymentsCardExpirationInputLabel')}
                    required
                    helper={lt('ProcessPaymentMewsPaymentsCardExpirationInputHint')}
                />
            </Stack>
            <FormInputField name={TokenizeCardFormField.PaymentCardHolderName} label={lt('CardHolderName')} />
        </Stack>
    );
};

const PciProxyCardNumberContainer = () => (
    <div
        data-test-id="field-pci-proxy-card-number"
        id={CARD_NUMBER_FIELD_ID}
        style={{
            width: '100%',
            height: '37px',
        }}
    />
);
const PciProxyCardNumberField = withField(PciProxyCardNumberContainer);

interface PaymentCardNumberFieldProps extends FieldProps {
    pciProxy: PciProxyService;
}

const PaymentCardNumberField = ({ pciProxy, name, ...other }: PaymentCardNumberFieldProps) => {
    const {
        field: { onChange, onBlur },
        fieldState: { error },
    } = useController({ name });

    useEffect(() => {
        pciProxy.registerCardChange((data) => {
            onChange(data);
        });
        pciProxy.registerCardBlur(onBlur);
    }, [onChange, onBlur, pciProxy]);

    return <PciProxyCardNumberField name={name} error={getErrorComponent(error)} {...other} />;
};
